import {
  TableContainer,
  Table as MuiTable,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { RenderTableRow } from 'utils/renderTableRow';
import PropTypes from 'prop-types';

export const Table = ({
  name,
  rows = [],
  header,
  hasCheckbox,
  paginate = {},
  actions,
  isLoading,
  hasPagination = true,
}) => {
  const { page, rowsPerPage, count, onPageChange, onRowsPerPageChange, scrollable } = paginate;
  const { order, orderBy } = paginate;
  const emptyRows = page >= 1 ? Math.max(0, page * rowsPerPage - count) : 0;

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }
  function stableSort(array, comparator) {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }
  const createSortHandler = (property) => (event) => {
    actions?.handleRequestSort(event, property);
  };

  const rowsPerPageOptions = [10, 25, 50];
  const pagesCount = Math.ceil(count / rowsPerPage);

  return (
    <>
      <TableContainer>
        <MuiTable sx={{ minWidth: 750 }} size="medium">
          <TableHead>
            <TableRow>
              {header.map((headCell, index) => (
                  <TableCell
                    key={index}
                    align={headCell.align || 'center'}
                    sortDirection={orderBy === headCell.name?.toLowerCase() ? order : 'asc'}
                    sx={{ width: headCell?.width || undefined, minWidth: headCell?.minWidth || undefined }}
                    width={150}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.name?.toLowerCase()}
                      direction={orderBy === headCell.name ? order : 'asc'}
                      onClick={createSortHandler(headCell.name)}
                      disabled={!headCell.isSortable}
                    >
                      {headCell.label}
                      {orderBy === headCell.name ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          {count !== 0 && (
            <TableBody>
              {isLoading ? (
                <TableRow sx={{ height: 550 }}>
                  <TableCell>
                    <Box
                      sx={{
                        position: 'relative',
                        left: '50rem',
                      }}
                    >
                      <CircularProgress size={100} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                stableSort(rows, getComparator(order, orderBy))?.map((row, index) => RenderTableRow(name, row, index, actions, hasCheckbox))
              )}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          )}
        </MuiTable>
      </TableContainer>
      {!isLoading && count === 0 && (
        <Stack
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            my: 7,
          }}
        >
          {/* <NoItemIllustration /> */}
          <Typography variant="body1" sx={{ my: 5 }}>
            موردی یافت نشد
          </Typography>
        </Stack>
      )}
      {!scrollable && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {hasPagination && (
            <>
              <Pagination
                color="primary"
                count={pagesCount}
                page={parseInt(page)}
                onChange={onPageChange}
                siblingCount={2}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Typography>{`تعداد کل‌ : ${count}`}</Typography>
                <FormControl style={{ width: 100 }}>
                  <InputLabel>در هر صفحه</InputLabel>
                  <Select label="در هر صفحه" value={rowsPerPage} onChange={onRowsPerPageChange}>
                    {rowsPerPageOptions.map((item, index) => (
                      <MenuItem value={item} key={index}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </>
          )}
        </Stack>
      )}
    </>
  );
};

Table.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  header: PropTypes.array.isRequired,
  hasCheckbox: PropTypes.bool,
  paginate: PropTypes.object,
  actions: PropTypes.object,
  isLoading: PropTypes.bool,
};

export { Toolbar } from './Toolbar';
export { TableMoreMenu } from './TableMoreMenu';
